import React, { useState, useEffect, useCallback } from 'react';
import {
    Users, Activity, Box, RefreshCw, BookOpen, Share2, Search,
    FileText, BarChart2, Server, TrendingUp, Mail, Cpu, AlertCircle, Fingerprint,
    Globe, Map
} from 'lucide-react';
import { analyticsApi, type DashboardMetrics } from '../lib/api/analytics';

export const DashboardPage: React.FC = () => {
    const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [pageSearch, setPageSearch] = useState('');

    const fetchData = useCallback(async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        try {
            const data = await analyticsApi.getDashboardMetrics(30).catch(() => null);
            setMetrics(data);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const interval = setInterval(() => fetchData(true), 60000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const pageStats = metrics?.appStats?.featureUsage?.pageStats || [];
    const chartStats = metrics?.appStats?.featureUsage?.chartStats || [];
    const channelDist = metrics?.appStats?.channels?.distribution || [];
    const userLocations = metrics?.appStats?.users?.locations || [];
    const visitorLocations = metrics?.appStats?.visitorStats?.topLocations || [];

    const filteredPages = pageStats.filter((p: any) => p.type.toLowerCase().includes(pageSearch.toLowerCase()));

    const formatNumber = (num?: number) => (num || 0).toLocaleString();

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2">
                        <Activity className="text-blue-600" />
                        Platform Overview
                    </h1>
                    <p className="text-neutral-500 text-sm">Monitor global system usage, feature adoption, and operational metrics.</p>
                </div>
                <button
                    onClick={() => fetchData(true)}
                    disabled={refreshing}
                    className="flex items-center gap-2 px-3 py-1.5 bg-white border border-neutral-200 rounded-lg text-xs font-medium text-neutral-600 hover:bg-neutral-50 transition-colors disabled:opacity-50"
                >
                    <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                    {refreshing ? 'Refreshing...' : 'Refresh Stats'}
                </button>
            </div>

            {/* Top Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm flex flex-col justify-between">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Users size={14} /> Total Users
                    </div>
                    <div className="text-2xl font-bold text-neutral-900 mt-1">
                        {loading ? '...' : formatNumber(metrics?.appStats?.users?.total)}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400 border-t border-neutral-100 pt-2 flex justify-between">
                        <span>Total platform accounts</span>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm flex flex-col justify-between">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Box size={14} /> Total Operations
                    </div>
                    <div className="text-2xl font-bold text-neutral-900 mt-1">
                        {loading ? '...' : formatNumber(metrics?.appStats?.operations?.total)}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400 border-t border-neutral-100 pt-2 flex justify-between">
                        <span title="Created"><span className="text-green-600 font-bold">+{formatNumber(metrics?.appStats?.operations?.created)}</span> C</span>
                        <span title="Updated"><span className="text-blue-500 font-bold">{formatNumber(metrics?.appStats?.operations?.updated)}</span> U</span>
                        <span title="Deleted"><span className="text-red-500 font-bold">-{formatNumber(metrics?.appStats?.operations?.deleted)}</span> D</span>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm flex flex-col justify-between">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                        <BookOpen size={14} /> Total Journals
                    </div>
                    <div className="text-2xl font-bold text-neutral-900 mt-1">
                        {loading ? '...' : formatNumber(metrics?.appStats?.journals?.total)}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400 border-t border-neutral-100 pt-2 flex justify-between">
                        <span title="Created"><span className="text-green-600 font-bold">+{formatNumber(metrics?.appStats?.journals?.created)}</span> C</span>
                        <span title="Updated"><span className="text-blue-500 font-bold">{formatNumber(metrics?.appStats?.journals?.updated)}</span> U</span>
                        <span title="Deleted"><span className="text-red-500 font-bold">-{formatNumber(metrics?.appStats?.journals?.deleted)}</span> D</span>
                    </div>
                </div>
                <div className="bg-white p-5 rounded-xl border border-neutral-200 shadow-sm flex flex-col justify-between">
                    <div className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2 flex items-center gap-2">
                        <Share2 size={14} /> Connected Channels
                    </div>
                    <div className="text-2xl font-bold text-neutral-900 mt-1">
                        {loading ? '...' : formatNumber(metrics?.appStats?.channels?.total)}
                    </div>
                    <div className="mt-2 text-[10px] text-neutral-400 border-t border-neutral-100 pt-2 flex justify-between">
                        <span title="Connected"><span className="text-green-600 font-bold">+{formatNumber(metrics?.appStats?.channels?.connected)}</span> C</span>
                        <span title="Updated"><span className="text-blue-500 font-bold">{formatNumber(metrics?.appStats?.channels?.updated)}</span> U</span>
                        <span title="Disconnected"><span className="text-red-500 font-bold">-{formatNumber(metrics?.appStats?.channels?.disconnected)}</span> D</span>
                    </div>
                </div>
            </div>

            {/* Feature Adoption Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Page Adoption Table */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="px-5 py-4 border-b border-neutral-100 bg-neutral-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                            <FileText size={16} className="text-neutral-500" />
                            Page Adoption
                        </h2>
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" size={14} />
                            <input
                                type="text"
                                value={pageSearch}
                                onChange={(e) => setPageSearch(e.target.value)}
                                placeholder="Search pages..."
                                className="pl-8 pr-3 py-1.5 text-xs border border-neutral-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-full sm:w-48"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto grow">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-neutral-50 text-neutral-500 text-[10px] uppercase font-bold border-b border-neutral-100">
                                <tr>
                                    <th className="px-5 py-3">Page Type</th>
                                    <th className="px-5 py-3 text-right">Unique Users</th>
                                    <th className="px-5 py-3 text-right">Total Instances</th>
                                    <th className="px-5 py-3 text-right">Adoption Rate</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-50">
                                {loading ? (
                                    <tr>
                                        <td colSpan={4} className="px-5 py-10 text-center text-neutral-400 text-xs italic">Loading pages...</td>
                                    </tr>
                                ) : filteredPages.length > 0 ? (
                                    filteredPages.map((page: any, i: number) => {
                                        const adoption = metrics?.appStats?.users?.total ? ((page.userCount / metrics.appStats.users.total) * 100).toFixed(1) : 0;
                                        return (
                                            <tr key={i} className="hover:bg-neutral-50/50 transition-colors">
                                                <td className="px-5 py-3 font-medium text-neutral-900">{page.type}</td>
                                                <td className="px-5 py-3 text-right text-neutral-600">{page.userCount}</td>
                                                <td className="px-5 py-3 text-right text-neutral-600">{page.boardCount}</td>
                                                <td className="px-5 py-3 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <span className="text-xs text-neutral-500">{adoption}%</span>
                                                        <div className="w-12 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                                                            <div className="h-full bg-blue-500" style={{ width: `${adoption}%` }} />
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan={4} className="px-5 py-10 text-center text-neutral-400 text-xs italic">No pages found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Chart Usage */}
                <div className="lg:col-span-1 bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="px-5 py-4 border-b border-neutral-100 bg-neutral-50/50">
                        <h2 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                            <BarChart2 size={16} className="text-neutral-500" />
                            Chart Components
                        </h2>
                    </div>
                    <div className="p-5 flex-1 overflow-y-auto max-h-[400px]">
                        {loading ? (
                            <div className="text-center text-neutral-400 text-xs italic py-10">Loading charts...</div>
                        ) : chartStats.length > 0 ? (
                            <div className="space-y-4">
                                {chartStats.map((chart: any, i: number) => {
                                    const maxCount = Math.max(...chartStats.map((c: any) => c.count), 1);
                                    const percentage = ((chart.count / maxCount) * 100).toFixed(1);
                                    return (
                                        <div key={i}>
                                            <div className="flex justify-between items-end mb-1">
                                                <span className="text-xs font-semibold text-neutral-700">{chart.type}</span>
                                                <span className="text-xs text-neutral-500">{chart.count} used</span>
                                            </div>
                                            <div className="w-full bg-neutral-100 h-2 rounded-full overflow-hidden">
                                                <div className="bg-indigo-500 h-full rounded-full transition-all" style={{ width: `${percentage}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="text-center text-neutral-400 text-xs italic py-10">No charts created yet.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Geography & Presence */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* User Locations */}
                <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 bg-neutral-50/50">
                        <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                            <Globe size={16} className="text-blue-500" />
                            User Base Locations
                        </h3>
                    </div>
                    <div className="p-5">
                        {loading ? (
                            <div className="text-neutral-400 text-xs italic">Loading...</div>
                        ) : userLocations.length > 0 ? (
                            <div className="space-y-3">
                                {userLocations.map((loc: any, i: number) => {
                                    const percentage = metrics?.appStats?.users?.total ? Math.round((loc.count / metrics.appStats.users.total) * 100) : 0;
                                    return (
                                        <div key={i} className="flex justify-between items-center group">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium text-neutral-700">{loc.country}</span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="w-24 h-1.5 bg-neutral-100 rounded-full overflow-hidden hidden sm:block">
                                                    <div className="h-full bg-blue-400 rounded-full" style={{ width: `${percentage}%` }} />
                                                </div>
                                                <span className="text-xs font-semibold text-neutral-900">{loc.count}</span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className="text-neutral-400 text-xs italic">No location data available.</div>
                        )}
                    </div>
                </div>

                {/* Top Visitor Locations */}
                <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-4 border-b border-neutral-100 bg-neutral-50/50">
                        <h3 className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                            <Map size={16} className="text-orange-500" />
                            Live Visitor Origins
                        </h3>
                    </div>
                    <div className="p-5">
                        {loading ? (
                            <div className="text-neutral-400 text-xs italic">Loading...</div>
                        ) : visitorLocations.length > 0 ? (
                            <div className="space-y-3">
                                {visitorLocations.map((loc: any, i: number) => {
                                    const max = Math.max(...visitorLocations.map((v: any) => v.count), 1);
                                    const scale = Math.round((loc.count / max) * 100);
                                    return (
                                        <div key={i} className="flex justify-between items-center group">
                                            <span className="text-xs font-medium text-neutral-700">{loc.country}</span>
                                            <div className="flex items-center gap-3">
                                                <div className="w-24 h-1.5 bg-neutral-100 rounded-full overflow-hidden hidden sm:block">
                                                    <div className="h-full bg-orange-400 rounded-full" style={{ width: `${scale}%` }} />
                                                </div>
                                                <span className="text-xs font-semibold text-neutral-900">{loc.count} hits</span>
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className="text-neutral-400 text-xs italic">No visitor location data tracked recently.</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Infrastructure & Ops */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Channel Distribution */}
                <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5">
                    <h3 className="text-sm font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                        <Server size={16} className="text-emerald-500" />
                        Channel Setup
                    </h3>
                    <div className="space-y-3">
                        {loading ? (
                            <div className="text-neutral-400 text-xs italic">Loading...</div>
                        ) : channelDist.length > 0 ? (
                            channelDist.map((ch: any, i: number) => {
                                const percentage = metrics?.appStats?.channels?.total ? Math.round((ch.count / metrics.appStats.channels.total) * 100) : 0;
                                return (
                                    <div key={i} className="flex justify-between items-center p-2 rounded bg-neutral-50">
                                        <span className="text-xs font-medium text-neutral-700 uppercase">{ch.type}</span>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs font-semibold text-neutral-900">{ch.count}</span>
                                            <span className="text-[10px] text-neutral-400 w-8 text-right">{percentage}%</span>
                                        </div>
                                    </div>
                                )
                            })
                        ) : (
                            <div className="text-neutral-400 text-xs italic">No channels connected.</div>
                        )}
                    </div>
                </div>

                {/* Secondary Feature Mix */}
                <div className="bg-white rounded-xl border border-neutral-200 shadow-sm p-5 flex flex-col gap-4 md:col-span-2">
                    <h3 className="text-sm font-semibold text-neutral-900 mb-1 flex items-center gap-2">
                        <TrendingUp size={16} className="text-neutral-500" />
                        Ancillary Metrics
                    </h3>
                    
                    <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-neutral-50 rounded border border-neutral-100 flex flex-col justify-between h-full">
                            <div>
                                <div className="text-[10px] font-semibold text-neutral-500 uppercase flex items-center gap-1 mb-1">
                                    <Cpu size={12} /> AI Usage
                                </div>
                                <div className="text-lg font-bold text-neutral-900">
                                    {formatNumber(metrics?.appStats?.ai?.activeUsers)} <span className="text-[10px] font-normal text-neutral-500 uppercase">Users</span>
                                </div>
                            </div>
                            <div className="text-[10px] text-neutral-400 mt-2 border-t border-neutral-200 pt-2 flex justify-between">
                                <span>{formatNumber(Math.round(metrics?.appStats?.ai?.avgTokensPerUser || 0))} avg/user</span>
                                <span>{formatNumber(metrics?.appStats?.ai?.totalTokens)} tot</span>
                            </div>
                        </div>
                        
                        <div className="p-3 bg-neutral-50 rounded border border-neutral-100">
                            <div className="text-[10px] font-semibold text-neutral-500 uppercase flex items-center gap-1 mb-1">
                                <Mail size={12} /> Email
                            </div>
                            <div className="text-lg font-bold text-neutral-900">{formatNumber(metrics?.appStats?.email?.totalSent)}</div>
                            <div className="text-[10px] text-neutral-400 mt-1">
                                {metrics?.appStats?.email?.totalSent ? Math.round((metrics.appStats.email.totalOpened / metrics.appStats.email.totalSent) * 100) : 0}% Open Rate
                            </div>
                        </div>

                        <div className="p-3 bg-neutral-50 rounded border border-neutral-100">
                            <div className="text-[10px] font-semibold text-neutral-500 uppercase flex items-center gap-1 mb-1">
                                <Fingerprint size={12} /> Sync Ops
                            </div>
                            <div className="text-lg font-bold text-neutral-900">{formatNumber(metrics?.appStats?.sync?.totalTransactions)}</div>
                            <div className="text-[10px] text-neutral-400 mt-1">Transactions</div>
                        </div>

                        <div className="p-3 bg-red-50 rounded border border-red-100">
                            <div className="text-[10px] font-semibold text-red-500 uppercase flex items-center gap-1 mb-1">
                                <AlertCircle size={12} /> Login Drops
                            </div>
                            <div className="text-lg font-bold text-red-700">{formatNumber(metrics?.appStats?.security?.recentFailures)}</div>
                            <div className="text-[10px] text-red-400 mt-1">Auth failures</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
